import { LoadingButton } from '@mui/lab';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ChatLoader from 'src/components/loading/chat-loader.component';
import SetupHeaderCard from 'src/components/setup/setup-header-card.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const defaultSettings = {
  is_enabled: true,
  welcome_message: 'Hi there! How can we help you today?',
  offline_message: 'Our team is currently offline. Please leave a message.',
  response_timeout_minutes: 15,
  send_ticket_created_email: true,
  ticket_created_template: `Hi {{name}},

Our support agents are not available right now. We created a support ticket {{ticketId}} and will respond as soon as possible.`,
};

export default function LiveChatSetup() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const [settings, setSettings] = useState(defaultSettings);
  const [settingsLoader, setSettingsLoader] = useState(true);
  const [saveLoader, setSaveLoader] = useState(false);
  const hasProfessionalTicketPlan = CommonHelper.HasProfessionalPlan();

  const fetchSettings = useCallback(() => {
    setSettingsLoader(true);
    HttpServices.Get({
      url: userModuleURL.liveChatSettings,
      successCallback: (data) => {
        setSettings({ ...defaultSettings, ...(data || {}) });
        setSettingsLoader(false);
      },
      failureCallback: (message) => {
        setSettingsLoader(false);
        message && showToastRef.current(message, 'e');
      },
    });
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveSettings = () => {
    setSaveLoader(true);
    HttpServices.Post({
      url: userModuleURL.liveChatSettings,
      body: { ...settings, is_enabled: true },
      successCallback: (data) => {
        setSettings({ ...defaultSettings, ...(data || {}) });
        setSaveLoader(false);
        showToastRef.current(
          translate('live_chat_setup_page.saved_successfully', {
            defaultValue: 'Live chat settings saved successfully',
          }),
          's'
        );
      },
      failureCallback: (message) => {
        setSaveLoader(false);
        message && showToastRef.current(message, 'e');
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>{translate('live_chat_setup_page.title', { defaultValue: 'Live Chat Setup' })}</title>
      </Helmet>

      <Box sx={{ height: 'calc(100vh - 70px)', minHeight: 0, overflow: 'hidden', pb: 2 }}>
        <Stack
          spacing={2.5}
          sx={{
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            px: 0.75,
            mx: 'auto',
            pb: { xs: 10, md: 6 },
            scrollbarGutter: 'stable both-edges',
          }}
        >
          <SetupHeaderCard
            icon={<ForumRoundedIcon />}
            title={translate('live_chat_setup_page.title', { defaultValue: 'Live Chat Setup' })}
            subtitle={translate('live_chat_setup_page.subtitle', {
              defaultValue: 'Configure real-time visitor chat behavior.',
            })}
          />

          <Paper
            variant="outlined"
            sx={{ p: 2.5, borderRadius: 2, flexShrink: 0 }}
          >
            <Stack spacing={2.5}>
              {settingsLoader ? (
                <ChatLoader
                  label={translate('live_chat_setup_page.loading', {
                    defaultValue: 'Loading live chat setup',
                  })}
                  description={translate('live_chat_setup_page.loading_description', {
                    defaultValue: 'Preparing your live chat experience',
                  })}
                />
              ) : (
                <Grid container spacing={2.5} sx={{ pr: { xs: 0.5, md: 1 }, pb: 1 }}>
                  {!hasProfessionalTicketPlan && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        {translate('live_chat_setup_page.basic_plan_timeout_notice', {
                          defaultValue:
                            'You are on the Basic plan. Timed-out chat ticket creation is available only on the Professional plan.',
                        })}
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label={translate('live_chat_setup_page.welcome_message', {
                        defaultValue: 'Welcome Message',
                      })}
                      value={settings.welcome_message}
                      disabled={saveLoader}
                      onChange={(e) =>
                        setSettings((prev) => ({ ...prev, welcome_message: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={translate('live_chat_setup_page.response_timeout', {
                        defaultValue: 'Agent response timeout (minutes)',
                      })}
                      value={settings.response_timeout_minutes}
                      disabled={saveLoader || !hasProfessionalTicketPlan}
                      onChange={(e) => {
                        const value = Math.min(1440, Math.max(1, Number(e.target.value) || 1));
                        setSettings((prev) => ({ ...prev, response_timeout_minutes: value }));
                      }}
                      inputProps={{ min: 1, max: 1440 }}
                      helperText={
                        hasProfessionalTicketPlan
                          ? translate('live_chat_setup_page.response_timeout_help', {
                              defaultValue:
                                'If no agent replies within this time, the conversation can follow your Ticket Setup rules.',
                            })
                          : translate('live_chat_setup_page.response_timeout_professional_only', {
                              defaultValue:
                                'Professional plan only: timed-out live chats can become tickets.',
                            })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label={translate('live_chat_setup_page.offline_message', {
                        defaultValue: 'Offline Message',
                      })}
                      value={settings.offline_message}
                      disabled={saveLoader}
                      onChange={(e) =>
                        setSettings((prev) => ({ ...prev, offline_message: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1, pr: { xs: 0.5, md: 1 } }}>
                      <LoadingButton
                        variant="contained"
                        loading={saveLoader}
                        onClick={saveSettings}
                      >
                        {translate('live_chat_setup_page.save_settings', {
                          defaultValue: 'Save Live Chat Settings',
                        })}
                      </LoadingButton>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </>
  );
}
